import { useCallback, useEffect, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Input } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import { Mail, Send, Server } from "lucide-react";

const EMPTY_FORM = {
  smtp_host: "",
  smtp_port: "587",
  smtp_username: "",
  smtp_password: "",
  from_email: "",
  from_name: "",
  use_tls: true,
  use_ssl: false,
  is_enabled: false,
};

export default function SmtpSettingsPanel({ apiBase, title, description, tourId }) {
  const { showNotification } = useApp();
  const [form, setForm] = useState(EMPTY_FORM);
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${apiBase}/smtp`);
      if (!res.ok) throw new Error("Failed to load SMTP settings");
      const data = await res.json();
      setForm({
        smtp_host: data.smtp_host || "",
        smtp_port: String(data.smtp_port ?? 587),
        smtp_username: data.smtp_username || "",
        smtp_password: "",
        from_email: data.from_email || "",
        from_name: data.from_name || "",
        use_tls: data.use_tls ?? true,
        use_ssl: data.use_ssl ?? false,
        is_enabled: data.is_enabled ?? false,
      });
      setPasswordConfigured(Boolean(data.password_configured));
    } catch (e) {
      showNotification(e.message || "Failed to load SMTP settings", "error");
    } finally {
      setLoading(false);
    }
  }, [apiBase, showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        smtp_host: form.smtp_host || null,
        smtp_port: Number(form.smtp_port) || 587,
        smtp_username: form.smtp_username || null,
        from_email: form.from_email || null,
        from_name: form.from_name || null,
        use_tls: form.use_tls,
        use_ssl: form.use_ssl,
        is_enabled: form.is_enabled,
      };
      if (form.smtp_password) body.smtp_password = form.smtp_password;

      const res = await apiFetch(`${apiBase}/smtp`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      const data = await res.json();
      setPasswordConfigured(Boolean(data.password_configured));
      setForm((f) => ({ ...f, smtp_password: "" }));
      showNotification("SMTP settings saved", "success");
    } catch (e) {
      showNotification(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      showNotification("Enter a test email address", "warning");
      return;
    }
    setTesting(true);
    try {
      const res = await apiFetch(`${apiBase}/smtp/test`, {
        method: "POST",
        body: JSON.stringify({ to_email: testEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Test failed");
      showNotification(data.message || "Test email sent", "success");
    } catch (e) {
      showNotification(e.message || "Test failed", "error");
    } finally {
      setTesting(false);
    }
  };

  return (
    <CollapsibleSettingsCard
      tourId={tourId || undefined}
      title={title}
      description={description}
      icon={<Server size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-blue-500 to-indigo-600"
    >
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-slate-100 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_enabled}
              onChange={(e) => setField("is_enabled", e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-700">Enable SMTP for outgoing email</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="SMTP host"
              value={form.smtp_host}
              onChange={(e) => setField("smtp_host", e.target.value)}
              placeholder="smtp.example.com"
            />
            <Input
              label="SMTP port"
              type="number"
              value={form.smtp_port}
              onChange={(e) => setField("smtp_port", e.target.value)}
            />
            <Input
              label="Username"
              value={form.smtp_username}
              onChange={(e) => setField("smtp_username", e.target.value)}
              autoComplete="off"
            />
            <Input
              label="Password"
              type="password"
              value={form.smtp_password}
              onChange={(e) => setField("smtp_password", e.target.value)}
              hint={
                passwordConfigured && !form.smtp_password
                  ? "Leave blank to keep existing password"
                  : undefined
              }
              autoComplete="new-password"
            />
            <Input
              label="From email"
              type="email"
              value={form.from_email}
              onChange={(e) => setField("from_email", e.target.value)}
              leftElement={<Mail size={14} />}
            />
            <Input
              label="From name"
              value={form.from_name}
              onChange={(e) => setField("from_name", e.target.value)}
              placeholder="OneDPDP"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.use_tls}
                onChange={(e) => {
                  setField("use_tls", e.target.checked);
                  if (e.target.checked) setField("use_ssl", false);
                }}
                className="rounded border-slate-300 text-blue-600"
              />
              Use STARTTLS (port 587)
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.use_ssl}
                onChange={(e) => {
                  setField("use_ssl", e.target.checked);
                  if (e.target.checked) setField("use_tls", false);
                }}
                className="rounded border-slate-300 text-blue-600"
              />
              Use SSL (port 465)
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2 border-t border-slate-100">
            <div className="flex-1 min-w-[200px]">
              <Input
                label="Send test email to"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleTest}
              loading={testing}
              disabled={saving}
              leftIcon={<Send size={14} />}
            >
              Send test
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={testing}>
              Save SMTP settings
            </Button>
          </div>
        </div>
      )}
    </CollapsibleSettingsCard>
  );
}

